import {
  useFeatureIsOn as useGBFeatureIsOn,
  useFeatureValue as useGBFeatureValue,
} from '@growthbook/growthbook-react';
import { useGrowthBookContext } from 'new/contexts/GrowthBookProvider';
import { useCurrentUser } from 'new/hooks/adapters/useCurrentUser';
import { useCurrentOrg } from 'new/hooks/adapters/useCurrentOrg';
import {
  FEATURE_FLAGS,
  FeatureFlagKey,
  FeatureFlagDefinition,
  getFeatureFlag,
  isValidFeatureFlag,
  DagModalsConfig,
} from 'new/constants/featureFlags';

// ============================================================================
// CORE FEATURE FLAG HOOKS
// ============================================================================

/**
 * Unified hook for evaluating any feature flag with automatic org-specific fallback
 * @param flagKey - The feature flag key from FEATURE_FLAGS
 * @returns boolean indicating if the feature is enabled
 */
export function useFeatureFlag(flagKey: FeatureFlagKey): boolean {
  const flag = getFeatureFlag(flagKey);
  const { isReady, instance } = useGrowthBookContext();
  const user = useCurrentUser();
  const org = useCurrentOrg();
  // Always call hook unconditionally (React rules)
  const gbFeatureIsOn = useGBFeatureIsOn(flag.key);

  // Return default value immediately if GrowthBook is not ready or no instance
  // This prevents delays in tests and ensures fast fallback
  if (!isReady || !instance) {
    return flag.defaultValue;
  }

  // Check if user has an org context
  const hasOrgContext = user && org && org.id;

  if (hasOrgContext) {
    // Try org-specific evaluation first
    const result = instance.evalFeature(flag.key);

    // If the feature is defined for this org (has a rule match), use that value
    if (result.source === 'experiment' || result.source === 'force') {
      return result.value;
    }
  }

  // Use instance directly when available for better performance
  // This avoids potential delays from the hook when instance is ready
  if (instance) {
    const result = instance.evalFeature(flag.key);
    return result.value ?? flag.defaultValue;
  }

  // Fallback to hook-based evaluation
  return gbFeatureIsOn;
}

/**
 * Unified hook for feature flags with JSON values and automatic org-specific fallback
 * @param flagKey - The feature flag key from FEATURE_FLAGS
 * @returns The feature flag value (JSON or boolean)
 */
export function useFeatureValue<T = any>(flagKey: FeatureFlagKey): T {
  const flag = getFeatureFlag(flagKey);
  const { isReady, instance } = useGrowthBookContext();
  const user = useCurrentUser();
  const org = useCurrentOrg();
  // Always call hook unconditionally (React rules)
  const gbFeatureValue = useGBFeatureValue(flag.key, flag.defaultValue as any);

  // Return default value immediately if GrowthBook is not ready or no instance
  // This prevents delays in tests and ensures fast fallback
  if (!isReady || !instance) {
    return flag.defaultValue;
  }

  // Check if user has an org context
  const hasOrgContext = user && org && org.id;

  if (hasOrgContext) {
    // Try org-specific evaluation first
    const result = instance.evalFeature(flag.key);

    // If the feature is defined for this org (has a rule match), use that value
    if (result.source === 'experiment' || result.source === 'force') {
      return result.value;
    }
  }

  // Use instance directly when available for better performance
  // This avoids potential delays from the hook when instance is ready
  if (instance) {
    const result = instance.evalFeature(flag.key);
    return (result.value ?? flag.defaultValue) as T;
  }

  // Fallback to hook-based evaluation
  return gbFeatureValue as T;
}

/**
 * Hook for feature flags with custom default values
 * @param flagKey - The feature flag key from FEATURE_FLAGS
 * @param defaultValue - Custom default value to override the configured default
 * @returns The feature flag value
 */
export function useFeatureFlagWithDefault<T = any>(flagKey: FeatureFlagKey, defaultValue: T): T {
  const flag = getFeatureFlag(flagKey);
  const { isReady, instance } = useGrowthBookContext();
  const user = useCurrentUser();
  const org = useCurrentOrg();

  // Return custom default value if GrowthBook is not ready or no instance
  if (!isReady || !instance) {
    return defaultValue;
  }

  // Check if user has an org context
  const hasOrgContext = user && org && org.id;

  if (hasOrgContext) {
    // Try org-specific evaluation first
    const result = instance.evalFeature(flag.key);

    // If the feature is defined for this org (has a rule match), use that value
    if (result.source === 'experiment' || result.source === 'force') {
      return result.value;
    }
  }

  // Fallback to environment-wide evaluation with custom default
  const value = useGBFeatureValue(flag.key, defaultValue as any);
  return value as T;
}

/**
 * Hook specifically for admin-only feature flags
 * Automatically checks if the current user is an admin
 * @param flagKey - The feature flag key from FEATURE_FLAGS
 * @returns boolean indicating if the feature is enabled for admin users
 */
export function useAdminFeatureFlag(flagKey: FeatureFlagKey): boolean {
  const user = useCurrentUser();
  const flag = getFeatureFlag(flagKey);

  // Validate that this is an admin-only flag
  if (flag.type !== 'admin-only') {
    console.warn(`useAdminFeatureFlag called with non-admin-only flag: ${flagKey}`);
  }

  // Check if user is admin first
  if (!user?.super_user) {
    return false;
  }

  return useFeatureFlag(flagKey);
}

// ============================================================================
// AUTO-GENERATED CONVENIENCE HOOKS
// ============================================================================

// Python 3 Support
export const usePython3Support = () => useFeatureFlag('UI_ENABLE_PYTHON3');

// Splitter Component
export const useSplitterSupport = () => useFeatureFlag('UI_ENABLE_SPLITTER');

// ui:display-flow-run-now
export const useDisplayFlowRunNow = () => useFeatureFlag('UI_DISPLAY_FLOW_RUN_NOW');
// ui dag modals
export const useUiDagModals = () => useFeatureValue('UI_DAG_MODALS');

// Multi-Block Transform JOLT Generation
export const useMultiBlockTransforms = () => useFeatureFlag('UI_ENABLE_MULTI_BLOCK_TRANSFORMS');

export const useSqlAutomation = () => useFeatureFlag('UI_ENABLE_SQL_AUTOMATION');

export const useOAuthServerFlow = () => useFeatureFlag('UI_OAUTH_SERVER_FLOW');

// ============================================================================
// UTILITY HOOKS
// ============================================================================

/**
 * Hook for conditional rendering based on feature flags
 * @param flagKey - The feature flag key from FEATURE_FLAGS
 * @returns Object with isEnabled boolean and render function
 */
export function useConditionalFeature(flagKey: FeatureFlagKey) {
  const isEnabled = useFeatureFlag(flagKey);

  return {
    isEnabled,
    // Utility function for conditional rendering
    render: (component: React.ReactNode) => (isEnabled ? component : null),
  };
}

/**
 * Hook to get feature flag information and metadata
 * @param flagKey - The feature flag key from FEATURE_FLAGS
 * @returns Object with feature flag definition and current value
 */
export function useFeatureFlagInfo(flagKey: FeatureFlagKey) {
  const flag = getFeatureFlag(flagKey);
  const isEnabled = useFeatureFlag(flagKey);
  const { isReady } = useGrowthBookContext();

  return {
    ...flag,
    isEnabled,
    isReady,
    // Helper for conditional rendering
    render: (component: React.ReactNode) => (isEnabled ? component : null),
  };
}

/**
 * Hook to get multiple feature flags at once
 * @param flagKeys - Array of feature flag keys
 * @returns Object with boolean values for each flag
 */
export function useMultipleFeatureFlags(flagKeys: FeatureFlagKey[]) {
  const result: Record<FeatureFlagKey, boolean> = {} as any;

  flagKeys.forEach((key) => {
    result[key] = useFeatureFlag(key);
  });

  return result;
}

/**
 * Hook to get all feature flags of a specific type
 * @param type - The feature flag type to filter by
 * @returns Object with boolean values for all flags of that type
 */
export function useFeatureFlagsByType(type: 'feature' | 'admin-only') {
  const allFlags = Object.keys(FEATURE_FLAGS) as FeatureFlagKey[];
  const filteredFlags = allFlags.filter((key) => FEATURE_FLAGS[key].type === type);

  return useMultipleFeatureFlags(filteredFlags);
}

// ============================================================================
// DEBUG AND DEVELOPMENT HOOKS
// ============================================================================

/**
 * Hook for debugging feature flags (development only)
 * @param flagKey - The feature flag key from FEATURE_FLAGS
 * @returns Detailed information about the feature flag
 */
export function useFeatureFlagDebug(flagKey: FeatureFlagKey) {
  const { instance, isReady } = useGrowthBookContext();
  const flag = getFeatureFlag(flagKey);

  if (!isReady || !instance) {
    return {
      flag,
      value: null,
      isReady: false,
      source: 'default',
      experiment: null,
    };
  }

  const result = instance.evalFeature(flag.key);

  return {
    flag,
    value: result.value,
    isReady: true,
    source: result.source,
    experiment: result.experiment,
    ruleId: result.ruleId,
  };
}

/**
 * Hook to get all feature flag states for debugging
 * @returns Object with all feature flag states
 */
export function useAllFeatureFlagsDebug() {
  const allFlags = Object.keys(FEATURE_FLAGS) as FeatureFlagKey[];
  const result: Record<FeatureFlagKey, any> = {} as any;

  allFlags.forEach((key) => {
    result[key] = useFeatureFlagDebug(key);
  });

  return result;
}

// ============================================================================
// LEGACY COMPATIBILITY
// ============================================================================

/**
 * Legacy hook for backward compatibility
 * @deprecated Use useFeatureFlag instead
 */
export function useFeatureIsOn(flagKey: string): boolean {
  if (isValidFeatureFlag(flagKey)) {
    return useFeatureFlag(flagKey as FeatureFlagKey);
  }

  // Fallback for non-typed flags
  const { isReady, instance } = useGrowthBookContext();

  if (!isReady || !instance) {
    return false; // Default to false for unknown flags
  }

  const isOn = useGBFeatureIsOn(flagKey);
  return isOn;
}

/**
 * Legacy hook for backward compatibility
 * @deprecated Use useFeatureValue instead
 */
export function useFeatureConfig<T = any>(flagKey: string, defaultValue: T): T {
  if (isValidFeatureFlag(flagKey)) {
    return useFeatureValue(flagKey as FeatureFlagKey);
  }

  // Fallback for non-typed flags
  const { isReady, instance } = useGrowthBookContext();

  if (!isReady || !instance) {
    return defaultValue;
  }

  const value = useGBFeatureValue(flagKey, defaultValue as any);
  return value as T;
}
